import { useEffect, useRef, useState } from "react";
import { Badge, Building, CalendarClock, CheckCircle, ChevronDown, Eye, FolderOpen, Info, Trash } from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { ROSTERING_GET } from "../../constants/api";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const Roster = () => {

    const [rosterData, setRosterData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false)
    let navigate = useNavigate()
    const { update } = useApiMethods()
    const { edit: editPermission, add: addPermission } = checkUserPermissionFunc("Roster", null, "Menu");

    const rosterColumns = [
        {
            accessor: 'from_date',
            Header: 'From Date',
            Cell: ({ value }) => value ?? "-"
        },
        {
            accessor: 'to_date',
            Header: 'To Date',
            Cell: ({ value }) => value ?? "-"
        },
        {
            accessor: 'department',
            Header: 'Departments',
            Cell: ({ value }) => {
                const [isOpen, setIsOpen] = useState(false);
                const buttonRef = useRef(null);
                const popupRef = useRef(null);

                useEffect(() => {
                    const handleClickOutside = (event) => {
                        if (popupRef.current &&
                            !popupRef.current.contains(event.target) &&
                            buttonRef.current &&
                            !buttonRef.current.contains(event.target)) {
                            setIsOpen(false);
                        }
                    };

                    document.addEventListener('mousedown', handleClickOutside);
                    return () => document.removeEventListener('mousedown', handleClickOutside);
                }, []);

                if (!value || value.length === 0) return '';

                return (
                    <div className="inline-block">
                        <Button
                            variant="ghost"
                            ref={buttonRef}
                            onClick={() => setIsOpen(!isOpen)}
                            className={`
      flex items-center gap-2
      border border-gray-200 dark:border-gray-700 
      bg-white dark:bg-gray-800
      hover:bg-gray-50 dark:hover:bg-gray-750
      text-xs font-medium
      text-gray-700 dark:text-gray-300
      px-3 py-2
      rounded-lg
      transition-all duration-200
      ${isOpen ? 'ring-2 ring-blue-500 ring-opacity-50 border-blue-400' : ''}
      shadow-sm hover:shadow
    `}
                        >
                            <Building className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                            <span>
                                {value.length} Department{value.length !== 1 ? 's' : ''}
                            </span>
                            <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                        </Button>
                        <div className=" relative">
                            {isOpen && (
                                <div
                                    ref={popupRef}
                                    className={`
        absolute left-0 mt-2
        bg-white dark:bg-gray-800
        rounded-xl shadow-xl
        border border-gray-100 dark:border-gray-700
        backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95
        z-50
        overflow-hidden
        min-w-[280px]
        animate-in fade-in slide-in-from-top-2 duration-200
      `}
                                    style={{ top: '100%' }}
                                >
                                    {/* Header */}
                                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-850">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                                                    Selected Departments
                                                </h4>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Scrollable content */}
                                    <div className="max-h-64 overflow-y-auto">
                                        <div className="p-2">
                                            {value.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-8 text-center">
                                                    <FolderOpen className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-2" />
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">No departments selected</p>
                                                </div>
                                            ) : (
                                                <ul className="space-y-1">
                                                    {value.map((dep, index) => (
                                                        <li
                                                            key={index}
                                                            className={`
                    flex items-center gap-3
                    text-sm px-3 py-2.5
                    rounded-lg
                    transition-all duration-150
                    hover:bg-blue-50 dark:hover:bg-blue-900/20
                    hover:translate-x-1
                    group
                    ${index % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-850/50' : ''}
                  `}
                                                        >
                                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-medium">
                                                                {index + 1}
                                                            </div>
                                                            <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                                                {dep}
                                                            </span>
                                                            <CheckCircle className="w-3.5 h-3.5 text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-850/50">
                                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                            <div className="flex items-center gap-1">
                                                <Info className="w-3.5 h-3.5" />
                                                <span>Click outside to close</span>
                                            </div>
                                            <div className="text-xs font-medium">
                                                Total: {value.length}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            accessor: 'total_employees',
            Header: 'Total Employee',
            Cell: ({ value }) => value ?? "-"
        },
        {
            accessor: 'is_published',
            Header: 'Publish Status',
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Published" : "Saved"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: 'action',
                Header: 'Action',
                Cell: ({ value, row }) => {

                    const handleOpenEdit = () => {
                        navigate('/roster/add', {
                            state: {
                                roster_id: row?.roster_id,
                                roster_from_date: row?.from_date,
                                roster_to_date: row?.to_date,
                                editAccess: editPermission,
                            }
                        });
                    }

                    const handleDelete = async () => {
                        try {
                            let payload = {
                                roster_id: row?.roster_id,
                                is_active: false,
                            }

                            const response = await update(ROSTERING_GET, payload)

                            if (response.status === 'success') {
                                ToastMsg('success', response.message)
                                setServerTrigger(true)
                            } else {
                                ToastMsg('error', response.message)
                            }
                        } catch (error) {
                            ToastMsg('success', error.message)
                        }
                    }

                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleOpenEdit}
                            >
                                <Eye className="h-4 w-4" />
                            </Button>
                            {
                                row.is_published === false && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                                        onClick={handleDelete}
                                    >
                                        <Trash className="h-4 w-4" />
                                    </Button>
                                )
                            }
                        </div>
                    )
                }
            }
        ] : [])
    ]

    const handleRosterOpen = () => {
        navigate("/roster/add")
    }

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={CalendarClock}
                title={"Roster"}
                subtitle={"Defining the Roster attendance"}
                buttonEnable={!!addPermission}
                buttonName="Add Roster"
                totalRecords={totalRecords}
                setOpenDialog={handleRosterOpen}
            />

            <Reactable
                columns={rosterColumns}
                data={rosterData}
                setData={setRosterData}
                setTotalCount={setTotalRecords}
                serverSide={true}
                serverTrigger={serverTrigger}
                url={ROSTERING_GET}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired
                isPDFRequired
            />
        </ContainerCard>
    )
}

export default Roster;